<template>
  <v-card class="secondary" height="100%">
    <v-form ref="form">
      <v-card-title class="font-weight-medium secondary--text primary">
        {{ edit ? `Edit Application Schedule` : "Add Application Schedule" }}
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-5">
        <v-row>
          <v-col cols="4">
            <v-autocomplete
              v-model="form.partner_id"
              :items="partners"
              item-text="name"
              item-value="id"
              label="Partner*"
              outlined
              dense
              :rules="[rules.required]"
              hide-details="auto"
              :readonly="viewPartner"
              :append-icon="viewPartner ? '' : 'mdi-menu-down'"
            ></v-autocomplete>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="showStartDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedApplicationStartDate"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  label="Application Start Date*"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.application_start_date"
                :min="minDate"
                @input="(showStartDatePicker = false)"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="showEndDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedApplicationEndDate"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  label="Application End Date*"
                  outlined
                  dense
                  hide-details="auto"
                  :disabled="!form.application_start_date"
                  :rules="[rules.required]"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.application_end_date"
                :min="form.application_start_date"
                @input="showEndDatePicker = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="showAdmissionEndDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedAdmissionEndDate"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  label="Admission End Date*"
                  outlined
                  dense
                  hide-details="auto"
                  :disabled="!(form.application_start_date && form.application_end_date)"
                  :rules="[rules.required]"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.admission_end_date"
                :min="form.application_start_date"
                @input="showAdmissionEndDatePicker = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="showCourseStartDatePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedCourseStartDate"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  label="Course Start Date*"
                  outlined
                  dense
                  hide-details="auto"
                  :disabled="!(form.application_start_date && form.application_end_date && form.admission_end_date)"
                  :rules="[rules.required]"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.course_start_date"
                :min="form.application_start_date"
                @input="showCourseStartDatePicker = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="4">
            <v-menu
              v-model="showCoursePayByDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedCoursePayByDate"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  label="Pay By Date*"
                  outlined
                  dense
                  hide-details="auto"
                  :rules="[rules.required]"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.pay_by_date"
                :min="form.application_end_date"
                @input="showCoursePayByDate = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="pt-0 mb-3 mx-2">
        <v-spacer></v-spacer>
        <v-btn class="primary" @click="save">{{
          edit ? "Update" : "Save"
        }}</v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

export default {
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    partners: {
      type: Array,
      default: () => [],
    },
    programs: {
      type: Array,
      default: () => [],
    },
    edit: {
      type: Boolean,
      default: () => false,
    },
    viewPartner: {
      type: Boolean,
      default: () => false,
    },
    viewProgram: {
      type: Boolean,
      default: () => false,
    },
  },
  components: {},
  data() {
    return {
      rules,
      form: {},
      showStartDatePicker: false,
      showEndDatePicker: false,
      showCourseStartDatePicker: false,
      showAdmissionEndDatePicker: false,
      showCoursePayByDate: false,
    };
  },
  computed: {
    minDate() {
      return this.currentDate(new Date());
    },
    formattedApplicationStartDate: {
      get() {
        return this.formatDate(this.form.application_start_date);
      },
      set(value) {
        this.form.application_start_date = value;
      },
    },
    formattedApplicationEndDate: {
      get() {
        return this.formatDate(this.form.application_end_date);
      },
      set(value) {
        this.form.application_end_date = value;
      },
    },
    formattedAdmissionEndDate: {
      get() {
        return this.formatDate(this.form.admission_end_date);
      },
      set(value) {
        this.form.admission_end_date = value;
      },
    },
    formattedCourseStartDate: {
      get() {
        return this.formatDate(this.form.course_start_date);
      },
      set(value) {
        this.form.course_start_date = value;
      },
    },
    formattedCoursePayByDate: {
      get() {
        return this.formatDate(this.form.pay_by_date);
      },
      set(value) {
        this.form.pay_by_date = value;
      },
    },
  },
  created() {
    this.init();
  },
  methods: {
    async init() {
      if (this.edit || this.viewPartner) {
        this.form = { ...this.data };
      }
      if (this.viewProgram) {
        this.form = { ...this.data };
      }
    },
    save() {
      if (!this.$refs.form.validate()) return;

      if (this.edit) {
        this.$emit("update", this.form);
      } else {
        this.form.active = true;
        this.$emit("create", this.form);
      }
    },
    currentDate(date) {
      const d = new Date(date);
      let month = "" + (d.getMonth() + 1);
      let day = "" + d.getDate();
      const year = d.getFullYear();

      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;

      return [year, month, day].join("-");
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      let day = "" + d.getDate();
      let month = "" + (d.getMonth() + 1);
      let year = d.getFullYear();

      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;

      return [day, month, year].join("-");
    },
  },
};
</script>

<style scoped></style>
